// Translates between the visual builder's { nodes, edges } graph and the
// backend's linear { trigger, steps } journey config.
//
// The backend engine (src/services/journeys/engine.service.js) supports
// segment/event/schedule triggers and a linear chain of steps — sends,
// relative/absolute waits, goal checks, and exactly one branch (Condition
// Split or A/B Split) per step, whose two outputs always reconverge to the
// same next step — there is no real diverging-path execution. This module
// walks the graph the user actually drew (instead of creation order) and
// enforces that shape: a branch node's two outputs must each be either a
// single action node or a direct pass-through (no action on that side —
// "if delivered, just continue"), and both must lead back to the same
// downstream node, or saving throws a clear error rather than building a
// config that would silently mis-execute.

import { propFiltersToProperties, propertiesToPropFilters } from './segmentConditions';

const ACTION_NODE_TO_CHANNEL = {
  'action-push': 'push',
  'action-whatsapp': 'whatsapp',
  'action-email': 'email',
  'action-sms': 'sms',
  'action-rcs': 'rcs',
};

const CHANNEL_TO_ACTION_NODE = Object.fromEntries(
  Object.entries(ACTION_NODE_TO_CHANNEL).map(([node, channel]) => [channel, node])
);

const CONDITION_TYPES = ['if_not_opened', 'if_delivered', 'if_failed'];

function splitParams(csv) {
  return (csv || '').split(',').map((s) => s.trim()).filter(Boolean);
}

// node.cfg shapes match campaign.validator.js's real per-channel Joi schemas
// (except action-inapp, which has no campaign equivalent) — see
// data/nodeDefinitions.js's defaultCfg(). This builds the exact `template`
// shape the backend (and the real provider layer) expects.
function buildTemplate(nodeType, cfg) {
  switch (nodeType) {
    case 'action-whatsapp':
      return {
        templateName: cfg.templateName || '',
        templateParams: splitParams(cfg.templateParams),
        ...(cfg.imageUrl ? { imageUrl: cfg.imageUrl } : {}),
      };
    case 'action-sms':
      return { text: cfg.text || '', ...(cfg.templateId ? { templateId: cfg.templateId } : {}) };
    case 'action-email':
      return { subject: cfg.subject || '', html: cfg.html || '' };
    case 'action-rcs':
      return { templateId: cfg.templateId || '', templateParams: splitParams(cfg.templateParams) };
    case 'action-push':
      return {
        title: cfg.title || '', body: cfg.body || '',
        ...(cfg.redirectionType ? { redirectionType: cfg.redirectionType } : {}),
        ...(cfg.redirectionValue ? { redirectionValue: cfg.redirectionValue } : {}),
        ...(cfg.imageUrl ? { imageUrl: cfg.imageUrl } : {}),
      };
    default:
      return cfg;
  }
}

// attributionLinkId is a sibling of the step/action object (step.attribution_link_id),
// not part of `template` itself — see buildActionStepAction's own comment.
// Only merged in for the channels ShortLinkField actually renders on.
function templateToCfg(channel, template, attributionLinkId) {
  const t = template || {};
  const linkCfg = ['sms', 'whatsapp', 'rcs'].includes(channel) ? { attributionLinkId: attributionLinkId || '' } : {};
  switch (channel) {
    case 'whatsapp':
      return { templateName: t.templateName || '', templateParams: (t.templateParams || []).join(', '), imageUrl: t.imageUrl || '', ...linkCfg };
    case 'sms':
      return { text: t.text || '', templateId: t.templateId || '', ...linkCfg };
    case 'email':
      return { subject: t.subject || '', html: t.html || '' };
    case 'rcs':
      return { templateId: t.templateId || '', templateParams: (t.templateParams || []).join(', '), ...linkCfg };
    case 'push':
    default:
      return { title: t.title || '', body: t.body || '', redirectionType: t.redirectionType || '', redirectionValue: t.redirectionValue || '', imageUrl: t.imageUrl || '' };
  }
}

function successorId(nodeId, handle, edges) {
  const edge = edges.find((e) => e.from === nodeId && (e.fromHandle || 'out') === (handle || 'out'));
  return edge ? edge.to : null;
}

function buildActionStepAction(node) {
  const channel = ACTION_NODE_TO_CHANNEL[node.type];
  if (!channel) return null;
  return {
    action: 'send_message', channel, template_id: node.id, template: buildTemplate(node.type, node.cfg),
    // Sibling of template, not nested inside it — see ShortLinkField.jsx's
    // own comment on why (it'd otherwise ride along into the actual
    // WhatsApp/SMS/RCS send payload).
    ...(node.cfg.attributionLinkId ? { attribution_link_id: node.cfg.attributionLinkId } : {}),
  };
}

function buildWaitUntil(cfg) {
  return {
    rule: cfg.rule,
    ...(cfg.tz ? { tz: cfg.tz } : {}),
    ...(cfg.rule === 'custom' ? { custom: { date: cfg.customDate, time: cfg.customTime } } : {}),
  };
}

function buildTrigger(entryNode) {
  const cfg = entryNode?.cfg || {};
  if (!entryNode || !entryNode.type.startsWith('entry-')) {
    throw new Error('Add a trigger (Entry) node before saving.');
  }
  if (entryNode.type === 'entry-segment') {
    if (!cfg.segmentId) throw new Error('Pick a segment for the entry trigger.');
    return { type: 'segment', segment_id: cfg.segmentId, evaluate_every: '15min' };
  }
  if (entryNode.type === 'entry-event') {
    if (!cfg.event) throw new Error('Pick a trigger event for the Event Trigger.');
    return {
      type: 'event',
      event_name: cfg.event,
      lookback_window: { value: Number(cfg.lookbackValue) || 7, unit: cfg.lookbackUnit || 'days' },
      properties: propFiltersToProperties(cfg.propFilters),
    };
  }
  if (entryNode.type === 'entry-schedule') {
    if (!cfg.cron) throw new Error('Enter a cron expression for the Schedule Entry.');
    if (!cfg.segmentId) throw new Error('Pick a segment for the Schedule Entry.');
    return { type: 'schedule', cron_expression: cfg.cron, timezone: cfg.tz, segment_id: cfg.segmentId };
  }
  throw new Error(`"${entryNode.type}" entry triggers aren't supported by the journey engine yet.`);
}

// Shared by Condition Split (yes/no) and A/B Split (a/b). Each output can be
// either a real action node (send something, then continue) or a direct
// pass-through straight to the shared continuation node (no action on that
// side) — e.g. "if failed, send a fallback SMS" only needs an action on the
// Yes side; No can just point at whatever comes next.
function resolveBranchPair(node, handleA, handleB, edges, byId, visited, label) {
  const aId = successorId(node.id, handleA, edges);
  const bId = successorId(node.id, handleB, edges);
  if (!aId || !bId) {
    throw new Error(`Connect both outputs of this ${label} before saving.`);
  }

  const resolveSide = (targetId) => {
    const action = buildActionStepAction(byId.get(targetId) || {});
    if (action) {
      visited.add(targetId);
      return { action, next: successorId(targetId, 'out', edges) };
    }
    return { action: null, next: targetId };
  };

  const a = resolveSide(aId);
  const b = resolveSide(bId);
  if (a.next !== b.next) {
    throw new Error(`This ${label}'s two branches must lead to the same next step — the journey engine can't yet run diverging paths after a branch.`);
  }
  return { aAction: a.action, bAction: b.action, next: a.next };
}

export function nodesToJourneyConfig(nodes, edges = []) {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const entryNode = nodes.find((n) => n.type.startsWith('entry-'));
  const trigger = buildTrigger(entryNode);

  const steps = [];
  const skipped = [];
  const visited = new Set([entryNode.id]);
  let pendingWait = null;
  let pendingWaitUntil = null;
  let stepNumber = 1;

  const withPending = (obj) => {
    const result = { ...obj, ...(pendingWait ? { wait_after: pendingWait } : {}), ...(pendingWaitUntil ? { wait_until: pendingWaitUntil } : {}) };
    pendingWait = null;
    pendingWaitUntil = null;
    return result;
  };

  let currentId = successorId(entryNode.id, 'out', edges);

  while (currentId) {
    if (visited.has(currentId)) {
      throw new Error('This journey has a loop — the engine can only run a straight (or single-branch) chain.');
    }
    visited.add(currentId);
    const node = byId.get(currentId);
    if (!node) break;

    if (node.type === 'wait-relative') {
      pendingWait = { value: Number(node.cfg.amount) || 1, unit: node.cfg.unit || 'hours' };
      currentId = successorId(node.id, 'out', edges);
      continue;
    }

    if (node.type === 'wait-absolute') {
      pendingWaitUntil = buildWaitUntil(node.cfg);
      currentId = successorId(node.id, 'out', edges);
      continue;
    }

    if (node.type === 'logic-goal') {
      if (!node.cfg.event) throw new Error('Pick a goal event for this Goal Check.');
      steps.push(withPending({ step: stepNumber++, goal_check: { event: node.cfg.event } }));
      currentId = successorId(node.id, 'out', edges);
      continue;
    }

    if (node.type === 'logic-condition') {
      if (!CONDITION_TYPES.includes(node.cfg.condType)) {
        throw new Error(`"${node.cfg.condType}" isn't evaluated by the journey engine yet — pick a supported condition or remove this Condition Split.`);
      }
      let refStepId;
      if (node.cfg.condType === 'if_delivered' || node.cfg.condType === 'if_failed') {
        const lastStep = steps[steps.length - 1];
        if (!lastStep?.action) {
          throw new Error('"Previous step delivered" / "Previous step failed" must directly follow an action step (Push, SMS, Email, WhatsApp, or RCS).');
        }
        refStepId = lastStep.template_id;
      }
      const { aAction, bAction, next } = resolveBranchPair(node, 'yes', 'no', edges, byId, visited, 'Condition Split');
      steps.push(withPending({
        step: stepNumber++,
        condition: node.cfg.condType,
        ...(refStepId ? { ref_step_id: refStepId } : {}),
        branches: { true: aAction, false: bAction },
      }));
      currentId = next;
      continue;
    }

    if (node.type === 'logic-ab') {
      const splitA = Number(node.cfg.splitA) || 50;
      const { aAction, bAction, next } = resolveBranchPair(node, 'a', 'b', edges, byId, visited, 'A/B Split');
      steps.push(withPending({ step: stepNumber++, ab_split: { split_a: splitA }, branches: { a: aAction, b: bAction } }));
      currentId = next;
      continue;
    }

    if (node.type === 'logic-exit') {
      break;
    }

    if (!ACTION_NODE_TO_CHANNEL[node.type]) {
      skipped.push(node.type);
      currentId = successorId(node.id, 'out', edges) || successorId(node.id, 'a', edges);
      continue;
    }

    steps.push(withPending({
      step: stepNumber++,
      action: 'send_message',
      channel: ACTION_NODE_TO_CHANNEL[node.type],
      template_id: node.id,
      template: buildTemplate(node.type, node.cfg),
      ...(node.cfg.attributionLinkId ? { attribution_link_id: node.cfg.attributionLinkId } : {}),
    }));
    currentId = successorId(node.id, 'out', edges);
  }

  if (!steps.length) {
    throw new Error('Add at least one action step (Push, SMS, Email, WhatsApp, or RCS).');
  }

  const orphans = nodes.filter((n) => !visited.has(n.id));
  if (orphans.length) {
    skipped.push(...orphans.map((n) => n.type));
  }

  return { config: { trigger, steps }, skipped };
}

function entryNodeFromTrigger(trigger, nextId) {
  const type = trigger?.type || 'segment';
  if (type === 'event') {
    return {
      type: 'entry-event',
      cfg: {
        event: trigger.event_name || '',
        lookbackValue: String(trigger.lookback_window?.value || 7),
        lookbackUnit: trigger.lookback_window?.unit || 'days',
        propFilters: propertiesToPropFilters(trigger.properties, nextId),
      },
    };
  }
  if (type === 'schedule') {
    return { type: 'entry-schedule', cfg: { cron: trigger.cron_expression || '', tz: trigger.timezone || 'IST +5:30', preset: '', segmentId: trigger.segment_id || '' } };
  }
  return { type: 'entry-segment', cfg: { segmentId: trigger?.segment_id || '' } };
}

function waitUntilToCfg(waitUntil) {
  return {
    rule: waitUntil.rule,
    tz: waitUntil.tz || 'IST +5:30',
    customDate: waitUntil.custom?.date || '',
    customTime: waitUntil.custom?.time || '',
  };
}

// Best-effort reverse mapping for loading an existing journey back into the
// canvas — reconstructs the entry node (matching the trigger type), one node
// per step (plus a preceding wait-relative/wait-absolute node, if the step
// had a wait_after/wait_until), a branch node with its two action nodes (or
// a direct pass-through edge, for a branch with no action) for any
// condition/ab_split step, and the edges connecting all of them in order.
export function journeyConfigToNodes(config) {
  let nodeCounter = 1;
  let edgeCounter = 1;
  const nextId = () => 'n' + nodeCounter++;
  const nextEdgeId = () => 'e' + edgeCounter++;
  const nodes = [];
  const edges = [];
  let col = 0;
  let row = 0;
  const place = () => {
    const pos = { x: 60 + col * 240, y: 60 + row * 160 };
    col = (col + 1) % 3;
    if (col === 0) row++;
    return pos;
  };
  const link = (fromId, toId, fromHandle) => {
    edges.push({ id: nextEdgeId(), from: fromId, to: toId, fromHandle: fromHandle || 'out' });
  };
  // Returns the id+handle that should feed into whatever comes next: a new
  // action node fed from (branchId, handle) if this branch has one, or
  // (branchId, handle) itself again when there's no action — the branch
  // node's own handle becomes a direct pass-through edge to the next node.
  const resolveBranchEnd = (action, branchId, handle) => {
    if (!action) return { from: branchId, handle };
    const id = nextId();
    nodes.push({ id, type: CHANNEL_TO_ACTION_NODE[action.channel] || 'action-push', ...place(), cfg: templateToCfg(action.channel, action.template, action.attribution_link_id) });
    link(branchId, id, handle);
    return { from: id, handle: 'out' };
  };

  const entryId = nextId();
  const entry = entryNodeFromTrigger(config?.trigger, nextId);
  nodes.push({ id: entryId, type: entry.type, ...place(), cfg: entry.cfg });
  let prevId = entryId;
  let prevHandle = 'out';
  let pendingBranchEnds = null; // [{from, handle}, ...]

  const linkPending = (toId) => {
    if (!pendingBranchEnds) return false;
    pendingBranchEnds.forEach(({ from, handle }) => link(from, toId, handle));
    pendingBranchEnds = null;
    return true;
  };

  for (const step of config?.steps || []) {
    let waitFromId = prevId;
    let waitFromHandle = prevHandle;

    if (step.wait_after) {
      const waitId = nextId();
      nodes.push({ id: waitId, type: 'wait-relative', ...place(), cfg: { amount: String(step.wait_after.value), unit: step.wait_after.unit, waitType: 'Fixed duration' } });
      if (!linkPending(waitId)) link(waitFromId, waitId, waitFromHandle);
      waitFromId = waitId; waitFromHandle = 'out';
    }
    if (step.wait_until) {
      const waitId = nextId();
      nodes.push({ id: waitId, type: 'wait-absolute', ...place(), cfg: waitUntilToCfg(step.wait_until) });
      if (!linkPending(waitId)) link(waitFromId, waitId, waitFromHandle);
      waitFromId = waitId; waitFromHandle = 'out';
    }
    prevId = waitFromId;
    prevHandle = waitFromHandle;

    if (step.goal_check) {
      const goalId = nextId();
      nodes.push({ id: goalId, type: 'logic-goal', ...place(), cfg: { event: step.goal_check.event } });
      if (!linkPending(goalId)) link(prevId, goalId, prevHandle);
      prevId = goalId; prevHandle = 'out';
      continue;
    }

    if (step.condition || step.ab_split) {
      const isAb = !!step.ab_split;
      const branchId = nextId();
      nodes.push({
        id: branchId,
        type: isAb ? 'logic-ab' : 'logic-condition',
        ...place(),
        cfg: isAb
          ? { splitA: String(step.ab_split.split_a ?? 50), splitB: String(100 - (step.ab_split.split_a ?? 50)) }
          : { condType: step.condition, event: '', timeWindow: 'ever' },
      });
      if (!linkPending(branchId)) link(prevId, branchId, prevHandle);

      const [handleA, handleB] = isAb ? ['a', 'b'] : ['yes', 'no'];
      const [actionA, actionB] = isAb ? [step.branches?.a, step.branches?.b] : [step.branches?.true, step.branches?.false];

      // The next step's node becomes both branches' target — stash both
      // "from" ends and connect them once that node exists.
      pendingBranchEnds = [
        resolveBranchEnd(actionA, branchId, handleA),
        resolveBranchEnd(actionB, branchId, handleB),
      ];
      prevId = null;
      prevHandle = null;
      continue;
    }

    const actionType = CHANNEL_TO_ACTION_NODE[step.channel] || 'action-push';
    const stepId = nextId();
    nodes.push({ id: stepId, type: actionType, ...place(), cfg: templateToCfg(step.channel, step.template, step.attribution_link_id) });
    if (!linkPending(stepId)) link(prevId, stepId, prevHandle);
    prevId = stepId;
    prevHandle = 'out';
  }

  return { nodes, edges };
}
